import { Obstacle } from "../Obstacle";
import { eObstacleType } from "../ObstacleTypes";
import { ObstacleMapData } from "../ObstacleMapData";
import { Gameplay, eBatchGroup, TIMER } from "../../../Gameplay";
import { ViewCellMapRow } from "../../View/ViewCellMapRow";
import { CannonObsSettings } from "./CannonObsSettings";
import { Player } from "../../Player/Player";
import { Game, eModelAsset, eSfxSprite, eAudioAsset, SfxSprites } from "../../../../Game";
import { RND } from "../../../../Utils/RND";
import { Pool } from "../../../../Utils/Collections/Pool";
import { ModelUtils } from "../../../../Utils/ModelUtils";
import { WrappedArray } from "../../../../Utils/Collections/WrapperArray";
import { SoundManager } from "../../../../Utils/SoundManager";



export class CannonObs extends Obstacle {
    private static _BEAM_FX_POOL: Pool<BeamFx>;

    /**
     * Velikost paprsku v metrech
     */
    private static readonly _BEAM_LEN = 14;
    private static readonly _BEAM_RADIUS = 0.14;

    /**
     * Offset paprsku na ose Y od pozice dela
     */
    private static readonly _BEAM_OFFSET_Y = 0.58;

    /**
     * Rychlost rotace hlav dela
     */
    private static readonly _HEAD_ROT_SPEED = 10;

    private _cannonHeads: pc.GraphNode[];

    /**
     * Pocatecni pozice dela (slouzi pro klepani s delem)
     */
    private _basePos: pc.Vec3;

    /**
     * Entita pro paprsek
     */
    private _beam: pc.Entity;

    /**
     * Aktualni velikost paprsku (0 az 1)
     */
    private _beamLen: number;

    /**
     * Pole s efekty paprsku
     */
    private _beamFxs: WrappedArray<BeamFx>;
    private _nextBeamFxTime: number;

    /**
     * Vzdalenost od hrace na ose Z, kdy dojde k vystreleni paprsku
     */
    private _shotDistance: number;

    /**
     * Vzdalenost od hrace, pri ktere bylo delo aktivovano
     */
    private _startDistance: number;

    /**
     * Cas, kdy doslo k vystrelu
     */
    private _shotTime: number;

    private _shakeDis: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        if (!CannonObs._BEAM_FX_POOL)
            CannonObs._BEAM_FX_POOL = new Pool<BeamFx>(BeamFx, 3, true);

        super(eObstacleType.cannon);

        SoundManager.createSoundComponent(this._entity, {
            positional: true,
            distanceModel: pc.DISTANCE_LINEAR,
            maxDistance: 10
        });

        SoundManager.addSoundSlot(this._entity, eSfxSprite.laserBeam, SfxSprites[eSfxSprite.laserBeam], Game.sfxSpriteAssetId);

        this._beam = new pc.Entity();
        this._beam.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.laserBeam, pc.ASSET_MODEL),
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.solid_0],
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            lightmapped: false,
        });

        this._beam.enabled = false;
        this._beam.setLocalPosition(0, CannonObs._BEAM_OFFSET_Y, 0);
        this._entity.addChild(this._beam);

        this._basePos = new pc.Vec3();
        this._beamFxs = new WrappedArray<BeamFx>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(mapData: ObstacleMapData, row: ViewCellMapRow): void {
        super.init(mapData, row);

        if (!this._cannonHeads) {
            this._cannonHeads = [
                this._entity.findByName("head_1"),
                this._entity.findByName("head_2")
            ];
        }

        this._startDistance = Player.instance.position.z - row.z;
        this._shotDistance = (<CannonObsSettings>mapData.settings).shotDistance;
        this._beamLen = 0;
        this._nextBeamFxTime = TIMER.time + 1000;
        this._shotTime = -1;

        this._basePos.copy(this._entity.getLocalPosition());
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    release(): void {
        super.release();

        this._beam.enabled = false;
        this._beamFxs.forEach((fx: BeamFx) => {
            fx.reset();
            CannonObs._BEAM_FX_POOL.returnItem(fx);
        });
        this._beamFxs.clear(false);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (!super.update())
            return false;

        let headRotSpeed = CannonObs._HEAD_ROT_SPEED;

        // Vzdalenost hrace od dela       
        let dis = Player.instance.position.z - this._row.z;

        if (dis < this._shotDistance) {
            if (this._shotTime < 0) {
                this._beam.enabled = true;
                this._shotTime = TIMER.time;

                this._shakeDis = -0.30;//0.30 * (RND.boolean() ? 1 : -1);
                this._entity.setLocalPosition(this._basePos.x, this._basePos.y, this._basePos.z + this._shakeDis);

                SoundManager.playSound(this._entity, eSfxSprite.laserBeam);
            }

            if (this._beamLen != 1) {
                let time = TIMER.time - this._shotTime;

                // Velikost paprsku                
                this._beamLen = time / 750;

                if (this._beamLen > 1)
                    this._beamLen = 1;

                this._beam.setLocalScale(1, 1, this._beamLen);

                // Zaklepani delem
                if (this._shakeDis != 0) {
                    let shakeProg = time / 750;

                    if (shakeProg > 1)
                        shakeProg = 1;

                    shakeProg = this._shakeDis - this._shakeDis * pc.ElasticOut(shakeProg);
                    this._entity.setLocalPosition(this._basePos.x, this._basePos.y, this._basePos.z + shakeProg);

                    if (shakeProg == 1)
                        this._shakeDis = 0;
                }
            }

            this._beam.rotateLocal(0, 0, 1080 * TIMER.deltaS);
        }
        else {
            // Cim bliz je delo k vystreleni, tim rychleji se toci
            headRotSpeed = pc.SineIn(1 - ((dis - this._shotDistance) / (this._startDistance - this._shotDistance))) * CannonObs._HEAD_ROT_SPEED;

            //this._entity.setLocalPosition(this._basePos.x + RND.realInRange(-0.05, 0.05), this._basePos.y, this._basePos.z + RND.realInRange(-0.05, 0.05));
        }

        // Rotace hlav
        this._cannonHeads[0].rotateLocal(0, 0, headRotSpeed);
        this._cannonHeads[1].rotateLocal(0, 0, -headRotSpeed);

        // Efekty paprsku
        this._beamFxs.forEach((fx: BeamFx) => {
            if (!fx.update()) {
                this._beamFxs.remove(false);
                CannonObs._BEAM_FX_POOL.returnItem(fx);
            }
        });

        if (TIMER.time > this._nextBeamFxTime) {
            this._nextBeamFxTime = TIMER.time + 200;
            this.showNewFx();
        }

        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci 3D kolizni oblast prekazky (bud pc.BoundingBox nebo pc.BoundingSphere)
     * @param pos Aktualni pozice prekazky v hernim svete
     * @param id Index kolizni oblasti
     */
    //-------------------------------------------------------------------------
    protected getColArea(pos: pc.Vec3, id: number): pc.BoundingBox | pc.BoundingSphere {
        if (id == 0)
            return super.getColArea(pos, id);

        let bb = Obstacle._TMP_BBOX;
        let halfLen = CannonObs._BEAM_LEN * this._beamLen / 2;

        bb.center.set(pos.x, pos.y, pos.z + halfLen);
        bb.halfExtents.set(CannonObs._BEAM_RADIUS, CannonObs._BEAM_RADIUS, halfLen);

        return bb;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private showNewFx(): void {
        let fx = CannonObs._BEAM_FX_POOL.getItem();

        fx.show(this._entity);
        this._beamFxs.add(fx, true);
    }
}



class BeamFx {
    private static readonly _LIFE_SPAN = 800;

    private static readonly _START_SCALE = 0.7;
    private static readonly _END_SCALE = 1.3;

    private static readonly _X = 0;
    private static readonly _Y = 0.58;

    private static readonly _START_Z = 0.35;
    private static readonly _END_Z = 1.35;

    private static readonly _START_ALPHA = 1;
    private static readonly _END_ALPHA = 0;

    private _entity: pc.Entity;
    private _actTime: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this._entity = new pc.Entity();
        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.fxLaser, pc.ASSET_MODEL),
            castShadows: false,
            receiveShadows: false,
            lightmapped: false,
        });
        this._entity.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    show(cannonEnt: pc.Entity): void {
        if (this._entity.parent != cannonEnt)
            this._entity.reparent(cannonEnt);

        this._entity.setLocalScale(BeamFx._START_SCALE, BeamFx._START_SCALE, BeamFx._START_SCALE);
        this._entity.setLocalPosition(BeamFx._X, BeamFx._Y, BeamFx._START_Z);
        ModelUtils.setOpacity(this._entity.model, BeamFx._START_ALPHA);
        this._entity.enabled = true;

        this._actTime = TIMER.time;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._entity.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        let progress = (TIMER.time - this._actTime) / BeamFx._LIFE_SPAN;

        if (progress >= 1) {
            this.reset();
            return false;
        }

        let scale = BeamFx._START_SCALE + progress * (BeamFx._END_SCALE - BeamFx._START_SCALE);

        this._entity.setLocalScale(scale, scale, scale);
        this._entity.setLocalPosition(BeamFx._X, BeamFx._Y, BeamFx._START_Z + pc.QuadraticOut(progress) * (BeamFx._END_Z - BeamFx._START_Z));
        ModelUtils.setOpacity(this._entity.model, BeamFx._START_ALPHA + progress * (BeamFx._END_ALPHA - BeamFx._START_ALPHA));

        return true;
    }
}